import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { AuthScaffold } from "./Login";
import { PasswordInput } from "@/components/PasswordInput";

export function ResetPassword() {
  const nav = useNavigate();
  const [ready, setReady] = useState(false);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    const sub = supabase.auth.onAuthStateChange((event, session) => {
      if (!active) return;
      if (event === "PASSWORD_RECOVERY" || (session && (event === "SIGNED_IN" || event === "INITIAL_SESSION"))) {
        setReady(true);
      }
    });
    void supabase.auth.getSession().then(({ data }) => { if (active && data.session) setReady(true); });
    const timeout = setTimeout(() => {
      if (active) setReady(prev => {
        if (!prev) setError("This reset link is invalid or has expired. Request a new one.");
        return prev;
      });
    }, 4000);
    return () => { active = false; clearTimeout(timeout); sub.data.subscription.unsubscribe(); };
  }, []);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null); setInfo(null);
    if (password.length < 6) { setError("Password must be at least 6 characters."); return; }
    if (password !== confirm) { setError("Passwords don't match."); return; }
    setBusy(true);
    // password_set tells /welcome this account has a real password of its own,
    // so an invited hire is never waved through without setting one.
    const { error } = await supabase.auth.updateUser({ password, data: { password_set: true } });
    setBusy(false);
    if (error) { setError(error.message); return; }
    setInfo("Password updated. Redirecting to sign in…");
    await supabase.auth.signOut();
    setTimeout(() => nav("/login", { replace: true }), 1200);
  }

  return (
    <AuthScaffold>
      <h1 className="mb-1 text-2xl font-bold tracking-tight">Set a new password</h1>
      <p className="mb-6 text-sm text-gray2">Enter and confirm your new password below.</p>
      {error && <div className="mb-4 rounded-lg bg-bad-bg px-3.5 py-2.5 text-sm text-bad">{error}</div>}
      {info && <div className="mb-4 rounded-lg bg-ok-bg px-3.5 py-2.5 text-sm text-ok">{info}</div>}
      {ready ? (
        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <label className="label">New password</label>
            <PasswordInput required minLength={6} value={password} onChange={e => setPassword(e.target.value)} autoFocus autoComplete="new-password" />
          </div>
          <div>
            <label className="label">Confirm new password</label>
            <PasswordInput required minLength={6} value={confirm} onChange={e => setConfirm(e.target.value)} autoComplete="new-password" />
          </div>
          <button className="btn w-full btn-lg" disabled={busy}>{busy ? "Updating…" : "Update password"}</button>
        </form>
      ) : (
        !error && <div className="text-sm text-gray2">Validating link…</div>
      )}
    </AuthScaffold>
  );
}
